import React, { useState } from 'react';
import { withBillingWriteAccess } from 'contexts/adminAccess';
import { useDataProvider, useRefresh, useNotify } from 'react-admin';
import { Form } from 'react-final-form';

import { Button, makeStyles } from '@material-ui/core';
import { UserDetails } from '../types';
import { Dialog } from 'components/common';
import { Input } from 'components/form';
import { minLength, maxLength, composeValidators } from 'components/form/validationRules';

const useStyles = makeStyles((theme) => ({
  button: {
    textTransform: 'none',
    color: theme.palette.secondary.contrastText,
  },
}));

interface Props {
  user: UserDetails;
}

const CardChargingBlockButton = ({ user }: Props) => {
  const { chargingBlockedAt, id: rolloId } = user;

  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const [loading, setLoading] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleEnableUser = async () => {
    try {
      setLoading(true);

      await dataProvider.delete('blockCardCharging', {
        userId: user.id,
      });

      refresh();
    } catch (error: any) {
      notify(
        typeof error === 'string' ? error : error.message || 'ra.notification.http_error',
        'warning'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDisableUser = async ({ note }) => {
    try {
      const response = await dataProvider.post('blockCardCharging', {
        data: note,
        userId: rolloId,
      });

      handleCloseDialog();
      refresh();

      return response;
    } catch (error: any) {
      notify(
        typeof error === 'string' ? error : error.message || 'ra.notification.http_error',
        'warning'
      );
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={!chargingBlockedAt ? handleOpenDialog : handleEnableUser}
        disabled={loading}
        className={classes.button}>
        {!chargingBlockedAt ? 'Block user' : 'Unblock user'}
      </Button>
      {openDialog && (
        <Form
          onSubmit={handleDisableUser}
          initialValues={{ note: '' }}
          render={({ handleSubmit, submitting }) => {
            return (
              <form onSubmit={handleSubmit}>
                <Dialog
                  open={openDialog}
                  onCancel={handleCloseDialog}
                  title="Block notes"
                  onConfirm={handleSubmit}
                  confirmText="Block"
                  submitting={submitting}>
                  <Input
                    name="note"
                    label="Note (optional)"
                    validate={composeValidators(minLength(5), maxLength(200))}
                  />
                </Dialog>
              </form>
            );
          }}
        />
      )}
    </>
  );
};

export default withBillingWriteAccess(CardChargingBlockButton);
